import { PlanetCardInfoType, PlanetLite } from '@apeiron/planet'
import { AnimatedBox, NextImage } from '@apeiron/temp'
import { ApeironSimpleButton } from '@apeiron/ui'
import { Box, Button, styled, Typography } from '@mui/material'
import ChangeImage from '@public/icons/icon-change.png'
import RemoveImage from '@public/icons/icon-remove.png'
import SelectImage from '@public/images/background-select.png'
import PlanetCard from '@src/components/planet/share/planetCard/PlanetCard'
import { StaticImageData } from 'next/image'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SelectContainer = styled(Box)`
  aspect-ratio: 0.649;
  position: relative;
  width: 165px;
`

const SelectButton = styled(Button)`
  aspect-ratio: 0.649;
  color: white;
  position: relative;
  text-transform: none;
  width: 95%;
  :disabled {
    opacity: 0.35;
  }
`

const SelectBackground = styled(NextImage)`
  height: 100.5%;
  position: absolute;
  width: 100%;
`

const SelectSign = styled(Typography)`
  color: #bbbbbb;
  font-size: 70px;
  font-weight: 400;
  left: 50%;
  position: absolute;
  top: 40%;
  transform: translate(-50%, -50%);
`

const SelectPlanet = styled(Typography)`
  color: #bbbbbb;
  font-size: 14px;
  font-weight: 300;
  left: 50%;
  line-height: 22px;
  position: absolute;
  top: 65%;
  transform: translate(-50%, -50%);
`

const ReselectContainer = styled(Box)`
  position: relative;
  aspect-ratio: 0.649;
  width: 165px;
`

const StyledPlanetCard = styled(PlanetCard)`
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
  }

  .Custom-Info-Header {
    top: 2%;
  }
  .Custom-Id {
    font-size: 10px;
    line-height: 10px;
    margin-bottom: unset;
  }
  .Custom-Name {
    font-size: 12px;
    line-height: 12px;
  }
  .Custom-Info-Text {
    font-size: 10px;
    line-height: 10px;
  }
`

const Overlay = styled(AnimatedBox)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 18px;
  height: 95%;
  left: -5%;
  justify-content: center;
  position: absolute;
  top: 0px;
  width: 105%;
`

const CustomOverlay = Overlay

const ReselectOverlay = styled(Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
`

const ReselectIcon = styled(NextImage)`
  aspect-ratio: 1;
  width: 60px;
`

const ReselectButton = styled(ApeironSimpleButton)`
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  border: 3px solid #576474;
  font-size: 14px;
  font-weight: 300;
  padding: 5px 35px;
  ${props => props.theme.breakpoints.up('res1024')} {
    min-width: unset;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
`

const RemoveButton = styled(Button)`
  position: absolute;
  right: -12px;
  top: -20px;
`

const RemoveIcon = styled(NextImage)`
  aspect-ratio: 1;
  width: 40px;
`

const PlanetSelectCard: FC<PlanetSelectCardProps> = props => {
  const {
    className,
    disableSelect,
    infoType,
    overlay,
    planet,
    showInfo,
    customBackgroundImage,
    customSelectText,
    onAdd,
    onChange,
    onRemove
  } = props

  const { t } = useTranslation()

  const [showOverlay, setShowOverlay] = useState<boolean>(false)

  const handleOnMouseOver = useCallback(() => {
    setShowOverlay(true)
  }, [])

  const handleOnMouseLeave = useCallback(() => {
    setShowOverlay(false)
  }, [])

  const handleOnChange = useCallback(() => {
    if (!R.isNil(planet) && onChange) {
      onChange(planet)
    }
  }, [planet, onChange])

  const handleOnRemove = useCallback(() => {
    if (onRemove && !R.isNil(planet)) {
      onRemove(planet)
    }
  }, [planet, onRemove])

  if (R.isNil(planet)) {
    return (
      <SelectContainer className={className}>
        <SelectButton
          className='Custom-Select-Button'
          disabled={disableSelect}
          onClick={onAdd}
        >
          <SelectBackground src={customBackgroundImage || SelectImage} />
          <SelectSign>+</SelectSign>
          <SelectPlanet>
            {customSelectText || t('planet.card.select')}
          </SelectPlanet>
        </SelectButton>
      </SelectContainer>
    )
  }

  if (!R.isNil(overlay)) {
    return (
      <ReselectContainer className={className}>
        <StyledPlanetCard
          className='Custom-Planet-Card'
          infoType={infoType}
          planet={planet}
          showInfo={showInfo}
          onClick={onChange}
        />
        <CustomOverlay className='Custom-Overlay'>{overlay}</CustomOverlay>
      </ReselectContainer>
    )
  }

  return (
    <ReselectContainer
      className={className}
      onMouseOver={handleOnMouseOver}
      onMouseLeave={handleOnMouseLeave}
    >
      <StyledPlanetCard
        className='Custom-Planet-Card'
        infoType={infoType}
        planet={planet}
        showInfo={showInfo}
        onClick={onChange}
      />
      <ReselectOverlay className='Custom-Reselect-Overlay' in={showOverlay}>
        <ReselectIcon className='Custom-Reselect-Icon' src={ChangeImage} />
        <ReselectButton
          className='Custom-Reselect-Button'
          onClick={handleOnChange}
        >
          {t('common.button.change')}
        </ReselectButton>
      </ReselectOverlay>
      {onRemove && (
        <RemoveButton className='Custom-Remove-Button' onClick={handleOnRemove}>
          <RemoveIcon className='Custom-Remove-Icon' src={RemoveImage} />
        </RemoveButton>
      )}
    </ReselectContainer>
  )
}

export type PlanetSelectCardProps = {
  className?: string
  disableSelect?: boolean
  infoType?: PlanetCardInfoType
  overlay?: ReactNode | null
  planet: PlanetLite | null
  showInfo?: boolean
  customBackgroundImage?: StaticImageData | string
  customSelectText?: string
  onAdd?: () => void
  onChange?: (planet: PlanetLite) => void
  onRemove?: (planet: PlanetLite) => void
}

export default PlanetSelectCard
